import { useEffect, useState } from 'react';
import { C } from '../../theme';
import { Icon } from '../../icons';
import { api, ApiError } from '../../api';
import type { TenantParty } from '../../api';
import { useToast } from '../../components/Toast';
import { Spinner } from '../../components/States';
import { Sheet, Field, TextInput, ErrorBanner, PrimaryBtn, GhostBtn } from './Sheet';

/**
 * Invite a tenant to sign in — and hand the owner a magic link to share.
 *
 * Setting the tenant party's email/phone (PATCH /parties/:id) is what lets them
 * OTP-log-in; the login identity IS the contact. The invite link deep-links the
 * sign-in screen with that identity pre-filled and the tenancy already bound
 * (the contact sits on this tenancy's tenant), so the tenant just taps for a
 * code. Shareable over WhatsApp, copyable, and re-openable any time.
 */
export function InviteTenant({
  partyId,
  tenantName,
  onClose,
  onSaved,
}: {
  partyId: string;
  tenantName?: string;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const notify = useToast();
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState<'edit' | 'share'>('edit');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [savedEmail, setSavedEmail] = useState('');
  const [savedPhone, setSavedPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Prefill from the current party record.
  useEffect(() => {
    let alive = true;
    api
      .getParty(partyId)
      .then((p: TenantParty) => {
        if (!alive) return;
        setEmail(p.email ?? '');
        setPhone(p.phone ?? '');
        setSavedEmail(p.email ?? '');
        setSavedPhone(p.phone ?? '');
      })
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [partyId]);

  async function save() {
    setError(null);
    if (!email.trim() && !phone.trim()) {
      setError('Add an email or phone. The tenant needs one to sign in.');
      return;
    }
    setBusy(true);
    try {
      await api.updateTenant(partyId, {
        email: email.trim() || null,
        phone: phone.trim() || null,
      });
      setSavedEmail(email.trim());
      setSavedPhone(phone.trim());
      onSaved?.();
      setPhase('share');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save the sign-in details.');
    } finally {
      setBusy(false);
    }
  }

  // The tenant signs in with their email if set, else phone.
  const contact = savedEmail || savedPhone;
  // Path-based invite (not a #hash) so WhatsApp/iMessage previews can be scraped;
  // /invite serves the invite card, then forwards the tenant into sign-in.
  const inviteLink = contact
    ? `${window.location.origin}/invite?id=${encodeURIComponent(contact)}`
    : '';
  const message = `Hi${tenantName ? ` ${tenantName}` : ''}, you can now view your tenancy on Premise: ledger, documents and requests. Sign in here (no password): ${inviteLink}`;
  const waDigits = savedPhone.replace(/[^\d]/g, '');
  const waUrl = `https://wa.me/${waDigits}?text=${encodeURIComponent(message)}`;

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(inviteLink);
      notify('Invite link copied');
    } catch {
      notify('Could not copy. Select and copy the link manually.');
    }
  }

  return (
    <Sheet
      icon="mail"
      title={phase === 'edit' ? 'Invite tenant' : 'Share the invite'}
      subtitle={tenantName}
      onClose={onClose}
      busy={busy}
      footer={
        phase === 'edit' ? (
          <>
            <GhostBtn onClick={onClose} disabled={busy}>Cancel</GhostBtn>
            <PrimaryBtn onClick={save} busy={busy}>Save &amp; get link</PrimaryBtn>
          </>
        ) : (
          <>
            <GhostBtn onClick={() => setPhase('edit')} disabled={busy}>Edit contacts</GhostBtn>
            <PrimaryBtn onClick={onClose}>Done</PrimaryBtn>
          </>
        )
      }
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '30px 0' }}><Spinner /></div>
      ) : phase === 'edit' ? (
        <>
          <ErrorBanner message={error} />
          <div style={{ fontSize: 13.3, color: C.ink2, lineHeight: 1.5 }}>
            The tenant signs in with this email or phone, no password. Enter either (or both); you'll get a
            link to send them next.
          </div>
          <Field label="Email">
            <TextInput value={email} onChange={setEmail} placeholder="tenant@example.com" type="email" inputMode="email" autoFocus />
          </Field>
          <Field label="Phone (for WhatsApp)">
            <TextInput value={phone} onChange={setPhone} placeholder="+91…" type="tel" inputMode="tel" />
          </Field>
        </>
      ) : (
        <>
          <div style={{ fontSize: 13.3, color: C.ink2, lineHeight: 1.5 }}>
            Send {tenantName ?? 'the tenant'} this link. Opening it takes them straight to sign-in with{' '}
            <b style={{ color: C.ink }}>{contact}</b> filled in. They just request a one-time code.
          </div>

          {/* link box */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: C.bg, border: `1px solid ${C.line}`, borderRadius: 10, padding: '10px 12px' }}>
            <Icon n="mail" size={15} color={C.muted} />
            <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: C.ink2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inviteLink}</span>
            <button onClick={copyLink} style={{ flex: 'none', border: `1px solid ${C.line}`, background: C.card, color: C.accent, fontWeight: 600, fontSize: 12, padding: '6px 11px', borderRadius: 8, cursor: 'pointer' }}>Copy</button>
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ flex: 1, minWidth: 150, textAlign: 'center', textDecoration: 'none', background: waDigits ? '#128c4b' : '#b7c2b8', color: '#fff', borderRadius: 11, padding: '11px 12px', fontWeight: 650, fontSize: 13.5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, pointerEvents: waDigits ? 'auto' : 'none' }}
            >
              <Icon n="chat" size={16} color="#fff" /> Share on WhatsApp
            </a>
            <button onClick={copyLink} style={{ flex: 1, minWidth: 120, border: `1px solid ${C.line}`, background: C.card, color: C.accent, borderRadius: 11, padding: '11px 12px', fontWeight: 650, fontSize: 13.5, cursor: 'pointer' }}>
              Copy link
            </button>
          </div>
          {!waDigits && (
            <div style={{ fontSize: 12, color: C.muted }}>Add a phone number to send this over WhatsApp.</div>
          )}
        </>
      )}
    </Sheet>
  );
}
